import { ErrorCode } from '@afterpaytouch/portal-api'
import { Route } from '../config/routes'
import { SupportedLocale } from './country'
import {
  VerifyRegistrationMobileEventRequest,
  VerifyRegistrationSMSAuthorizationResponse,
  VerifyRegistrationSMSAuthorizationCode,
  VerificationMethod,
} from './registration'
import { CodeVerificationDirective } from './auth'

// Re-export for use in components
export { VerificationMethod, VerifyRegistrationSMSAuthorizationCode }

export enum VerificationErrorCode {
  ErrorSendingCode,
  MethodNotEnabled,
  CodeInvalid,
  CodeExpired,
  MaxAttemptsReached,
  PasswordResetDeclined,
  MaxSmsCodeAttempts,
  MaxEmailCodeAttempts,
  InvalidAddress,
  DuplicateIdentity,
  ExistingPhoneNumber,
  ExistingPhoneNumberUnicorn,
  ExistingPhoneNumberSendEmail,
  Unknown,
  PasswordResetTwoFactorChallengesInvalid,
  PasswordResetTwoFactorChallengesMaxAttempts,
  InvalidDob,
  HighRiskNationality,
  IdentityCheckRejected,
  MaxAttemptsReachedForType,
  RegistrationNotPermitted,
  PaywatchCheckFailed,
  InvalidPhoneNumber,
  EfaCheckNotRequired,
  AccountDisabled,
  AccountClosed,
  PasswordResetNotAllowed,
  AccountCreationFailed,
}

// VerificationMethod is now imported and re-exported from registration

export class VerificationError extends Error {
  readonly code: VerificationErrorCode
  readonly isFatal: boolean
  readonly isSignUpError: boolean
  readonly isEfaNotRequired: boolean

  constructor(code: VerificationErrorCode = VerificationErrorCode.Unknown) {
    super(VerificationErrorCode[code])
    this.code = code
    this.isFatal = fatalErrors.has(code)
    this.isSignUpError = signUpErrors.has(code)
    this.isEfaNotRequired = code === VerificationErrorCode.EfaCheckNotRequired
  }

  static is(e: unknown): e is VerificationError {
    return typeof e === 'object' && e !== null && 'code' in e && typeof (e as { code: unknown }).code === 'number'
  }
}

const fatalErrors = new Set([
  VerificationErrorCode.MaxAttemptsReached,
  VerificationErrorCode.MaxSmsCodeAttempts,
  VerificationErrorCode.MaxEmailCodeAttempts,
  VerificationErrorCode.PasswordResetDeclined,
  VerificationErrorCode.MethodNotEnabled,
  VerificationErrorCode.DuplicateIdentity,
  VerificationErrorCode.HighRiskNationality,
  VerificationErrorCode.MaxAttemptsReachedForType,
  VerificationErrorCode.PaywatchCheckFailed,
  VerificationErrorCode.AccountClosed,
  VerificationErrorCode.AccountDisabled,
  VerificationErrorCode.PasswordResetNotAllowed,
])

const signUpErrors = new Set([
  VerificationErrorCode.InvalidAddress,
  VerificationErrorCode.ExistingPhoneNumber,
  VerificationErrorCode.ExistingPhoneNumberUnicorn,
  VerificationErrorCode.ExistingPhoneNumberSendEmail,
  VerificationErrorCode.IdentityCheckRejected,
  VerificationErrorCode.AccountCreationFailed,
])

// VerificationFlowContext's value should match localisations verify:description's context
export enum VerificationFlowContext {
  Registration = 'registration',
  FirstPassword = 'firstPassword',
  Reset = 'reset',
  Security = 'security',
  VerifyMobile = 'verifyMobile',
  Efa = 'efa',
}

export interface VerificationFlowRoute {
  route?: Route
  locale?: SupportedLocale
  triggerProcessingModal?: boolean
  deferPostRegistrationRouteUntilFeatureFlagsReady?: boolean
}

export const deferPostRegistrationRouteUntilFeatureFlagsReady = (locale?: SupportedLocale): VerificationFlowRoute => ({
  deferPostRegistrationRouteUntilFeatureFlagsReady: true,
  locale,
})

export interface VerificationFlow {
  allowLogout: boolean
  autoSendCode: boolean
  checkCode: (code: string, method: VerificationMethod) => Promise<VerificationFlowRoute>
  context: VerificationFlowContext
  enabled: Record<VerificationMethod, boolean>
  previousRoute?: Route
  revealMobile: boolean
  sendCode: (method: VerificationMethod) => Promise<CodeVerificationDirective | void>
  initialMethod?: VerificationMethod
  prefetchRoutes: Route[]
  checkDob?: (dateOfBirth: Date, lastName: string) => Promise<void>
  sendSmsCodeEvent?: (request: VerifyRegistrationMobileEventRequest) => Promise<void>
  sendSmsAuthorization?: () => Promise<VerifyRegistrationSMSAuthorizationResponse>
}

export interface VerificationCountdown {
  seconds: number
  minutes: number
}

export enum WaitingForEvents {
  'WAIT' = 'WAIT',
  'SUCCESS' = 'SUCCESS',
}

export interface VerifyTimeout {
  verifyTimeout: boolean
  startVerifyTimeoutTimer: (waitingFor: WaitingForEvents, waitingTime?: number) => void
  killVerifyTimeoutTimer: () => void
  waitingFor: WaitingForEvents
}

export const EFA_ERRORS: Partial<Record<ErrorCode, VerificationErrorCode>> = {
  [ErrorCode.EFACodeGenerationNotAllowed]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.EFACodeDeliveryFailure]: VerificationErrorCode.ErrorSendingCode,
  [ErrorCode.EFACodeUnableToBeSaved]: VerificationErrorCode.Unknown,
  [ErrorCode.EFACodeNotFound]: VerificationErrorCode.Unknown,
  [ErrorCode.EFACodeInvalid]: VerificationErrorCode.CodeInvalid,
  [ErrorCode.EFACodeMaxAttempts]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.MaxSmsCodeAttempts]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.MaxEmailCodeAttempts]: VerificationErrorCode.MaxAttemptsReached,
  [ErrorCode.MaxAttemptsReached]: VerificationErrorCode.MaxAttemptsReached,
} as const

export enum EFA_CHECK_STATUS {
  EFA_CHECK_SMS_SENT = 'EFA_CHECK_SMS_SENT',
  EFA_CHECK_NOT_REQUIRED = 'EFA_CHECK_NOT_REQUIRED',
}
